import React from "react";
import {
  KeyboardAvoidingView,
  Platform,
  SafeAreaView,
  ScrollView,
  Text,
  TextInput,
  TouchableOpacity,
  useWindowDimensions,
  View,
} from "react-native";
import { CreateRoomViewProps } from "../createRoom.types";
import SharedCareCard from "./SharedCareCard";
import TopicCard from "./TopicCars";
import DateTimePicker from "./DateTimePicker";
import { canProceedCreateRoom } from "../canProceed";
import { TOPICS } from "../../../constants/topics";
import {
  BETA_TIME_SLOT_NOTICE,
  MOBILE_STEP_ROW_WIDTH,
  sharedCareRules,
  styles,
  PREDEFINED_MEETING_NAMES,
} from "./CreateRoomView.shared";
import useStepScrollToTop from "../../../hooks/useStepScrollToTop";
import Dropdown from "../../../common/Dropdown";

export default function CreateRoomViewMobile({
  currentStep,
  onStepChange,
  onAgreeToTerms,
  selectedHour,
  selectedMinute,
  selectedAmPm,
  selectedDate,
  roomName,
  roomNameError,
  currentTimezone,
  onChangeHour,
  onChangeMinute,
  onChangeAmPm,
  onChangeDate,
  onChangeRoomName,
  selectedTopicIds,
  onToggleTopic,
  isSubmitting,
  onSubmit,
  onCancel,
  onBack,
  selectedMeetingNameOption,
  onChangeSelectedMeetingNameOption,
  maxParticipants,
  onChangeMaxParticipants,
}: CreateRoomViewProps) {
  const { width } = useWindowDimensions();
  const scrollRef = useStepScrollToTop(currentStep);
  const mobileStepLabel =
    currentStep === 1
      ? "SHARED CARE AGREEMENT"
      : currentStep === 2
        ? "ALL CONVERSATIONS"
        : "TOPICS";

  const renderStepIndicator = () => {
    const renderStep = (stepNumber: 1 | 2 | 3) => {
      const isActive = currentStep === stepNumber;
      const isCompleted = currentStep > stepNumber;
      return (
        <View style={styles.stepContainer}>
          <View
            style={[
              styles.stepCircle,
              (isActive || isCompleted) && styles.stepCircleActive,
            ]}
          >
            <Text
              style={[
                styles.stepNumber,
                (isActive || isCompleted) && styles.stepNumberActive,
              ]}
            >
              {stepNumber}
            </Text>
          </View>
        </View>
      );
    };

    return (
      <View style={styles.stepIndicatorWrapper}>
        <View
          style={[
            styles.stepIndicator,
            styles.stepIndicatorMobile,
            {
              maxWidth: Math.min(MOBILE_STEP_ROW_WIDTH, width - 32),
            },
          ]}
          pointerEvents="none"
        >
          {renderStep(1)}
          {renderStep(2)}
          {renderStep(3)}
        </View>
        <Text style={styles.stepLabelMobile}>{mobileStepLabel}</Text>
      </View>
    );
  };

  const renderStep1 = () => {
    return (
      <View style={[styles.stepContent, styles.stepContentMobile]}>
        <View style={[styles.gridContainer, styles.gridContainerMobile]}>
          {sharedCareRules.map((rule, index) => (
            <SharedCareCard
              key={index}
              title={rule.title}
              description={rule.description}
              icon={rule.icon}
              expandedContent={rule.expandedContent}
              variant="mobile"
            />
          ))}
        </View>
      </View>
    );
  };

  const renderStep2 = () => {
    return (
      <View style={[styles.stepContent, styles.stepContentMobile]}>
        <View style={[styles.formCard, styles.formCardMobile]}>
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>CONVERSATION NAME</Text>
            <Dropdown
              label="Select Predefined Name or Make Your Own"
              value={selectedMeetingNameOption}
              options={PREDEFINED_MEETING_NAMES}
              onChange={onChangeSelectedMeetingNameOption}
            />
          </View>

          {selectedMeetingNameOption === "Custom / Make your own..." && (
            <View style={styles.section}>
              <Text style={styles.sectionTitle}>CUSTOM CONVERSATION NAME</Text>
              <TextInput
                value={roomName}
                onChangeText={onChangeRoomName}
                placeholder="Let's talk about hobbies, Dreams, Healthy lifestyle"
                placeholderTextColor="#b1afaf"
                style={[
                  styles.input,
                  styles.inputMobile,
                  roomNameError && styles.inputError,
                ]}
              />
              {roomNameError && (
                <Text style={styles.errorText}>{roomNameError}</Text>
              )}
            </View>
          )}

          <View style={styles.section}>
            <Text style={styles.sectionTitle}>MAX PARTICIPANTS</Text>
            <Dropdown
              label="Select capacity (up to 4)"
              value={`${maxParticipants} participants`}
              options={["2 participants", "3 participants", "4 participants"]}
              onChange={(val) => {
                const count = parseInt(val.split(" ")[0], 10);
                onChangeMaxParticipants(count);
              }}
            />
          </View>

          <View style={styles.section}>
            <Text style={styles.sectionTitle}>PICK YOUR TIME</Text>
            <Text style={[styles.betaNotice, styles.betaNoticeMobile]}>
              {BETA_TIME_SLOT_NOTICE}
            </Text>
            <DateTimePicker
              selectedDate={selectedDate}
              selectedHour={selectedHour}
              selectedMinute={selectedMinute}
              selectedAmPm={selectedAmPm}
              onDateChange={onChangeDate}
              onHourChange={onChangeHour}
              onMinuteChange={onChangeMinute}
              onAmPmChange={onChangeAmPm}
              variant="mobile"
            />
            <Text style={[styles.helperText, styles.helperTextMobile]}>
              Time will be displayed in each participant&#39;s local timezone.
              Your timezone: {currentTimezone}
            </Text>
          </View>
        </View>
      </View>
    );
  };

  const renderStep3 = () => {
    return (
      <View style={[styles.stepContent, styles.stepContentMobile]}>
        <View
          style={[styles.topicsGridContainer, styles.topicsGridContainerMobile]}
        >
          {TOPICS.map((topic) => (
            <TopicCard
              key={topic.id}
              title={topic.label}
              description={topic.description}
              selected={selectedTopicIds.includes(topic.id)}
              onToggle={() => onToggleTopic(topic.id)}
              variant="mobile"
            />
          ))}
        </View>
      </View>
    );
  };

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 1:
        return renderStep1();
      case 2:
        return renderStep2();
      case 3:
        return renderStep3();
      default:
        return null;
    }
  };

  const canProceed = () =>
    canProceedCreateRoom(currentStep, {
      selectedMeetingNameOption,
      roomName,
      selectedHour,
      selectedMinute,
      selectedAmPm,
      selectedTopicIds,
    });

  const handleNext = () => {
    if (currentStep < 3) {
      onStepChange((currentStep + 1) as 1 | 2 | 3);
    } else {
      onSubmit();
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      onStepChange((currentStep - 1) as 1 | 2 | 3);
    } else {
      onBack();
    }
  };

  return (
    <SafeAreaView style={styles.root}>
      <KeyboardAvoidingView
        style={{ flex: 1 }}
        behavior={Platform.OS === "ios" ? "padding" : "height"}
      >
        <ScrollView
          ref={scrollRef}
          contentContainerStyle={styles.scrollContent}
          showsVerticalScrollIndicator={false}
        >
          <View style={[styles.content, styles.contentMobile]}>
            <View style={styles.mainContent}>
              <Text style={[styles.pageTitle, styles.pageTitleMobile]}>
                Create a New Conversation
              </Text>

              {renderStepIndicator()}

              {renderCurrentStep()}
            </View>

            <View style={[styles.footerSection, styles.footerSectionMobile]}>
              <View style={[styles.footer, styles.footerMobile]}>
                <TouchableOpacity
                  onPress={currentStep === 1 ? onCancel : handleBack}
                  style={[styles.cancelButton, styles.cancelButtonMobile]}
                >
                  <Text style={styles.cancelButtonText}>
                    {currentStep === 1 ? "CANCEL" : "BACK"}
                  </Text>
                </TouchableOpacity>
                {currentStep === 1 ? (
                  <TouchableOpacity
                    onPress={() => {
                      onAgreeToTerms();
                      onStepChange(2);
                    }}
                    style={[styles.continueButton, styles.continueButtonMobile]}
                  >
                    <Text style={styles.continueButtonText}>AGREE</Text>
                  </TouchableOpacity>
                ) : (
                  <TouchableOpacity
                    onPress={handleNext}
                    style={[
                      styles.continueButton,
                      styles.continueButtonMobile,
                      (!canProceed() || isSubmitting) &&
                        styles.continueButtonDisabled,
                    ]}
                    disabled={!canProceed() || isSubmitting}
                  >
                    <Text style={styles.continueButtonText}>
                      {isSubmitting ? "CREATING..." : "CONTINUE"}
                    </Text>
                  </TouchableOpacity>
                )}
              </View>
            </View>
          </View>
        </ScrollView>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
}
